import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Search, Play, Star, ChevronDown, Volume2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { listVoices, synthesizeSpeech } from '@/services/voiceService';
import { useApp } from '@/contexts/AppContext';
import CustomSelect from './CustomSelect';

const SAMPLE_TEXTS: Record<string, string> = {
  zh: '你好，这是我的声音样本。',
  en: 'Hello, this is my voice sample.',
  ja: 'こんにちは、これは私の声のサンプルです。',
  ko: '안녕하세요, 이것은 제 음성 샘플입니다.',
};

/** TTS API 返回的语言代码 → 中文显示名映射 */
const LANGUAGE_LABEL_MAP: Record<string, string> = {
  zh: '中文',
  en: '英语',
  ja: '日语',
  ko: '韩语',
  yue: '粤语',
  'Long Ang - graceful female': '英语（优雅女声）',
  'Long Anou - tender female': '英语（温柔女声）',
  'Long Han - affectionate male': '英语（深情男声）',
  'Korean Female - broadcasting': '韩语（新闻女主播）',
  'Korean Male - friendly': '韩语（亲切男声）',
};

/** TTS 语音英文原名 → 中文显示名映射（完整覆盖 cosyvoice + ark + gptsovits） */
const VOICE_NAME_MAP: Record<string, string> = {
  // ── CosyVoice / DashScope 预设 ──
  'Long Xiaochun - bright female': '晓春（明亮女声）',
  'Long Xiaoxia - calm female': '晓夏（沉静女声）',
  'YUMI - youthful female': 'YUMI（元气少女）',
  'Long Anyun - warm male': '安云（温暖男声）',
  'Long Anwen - elegant female': '安文（优雅女声）',
  'Long Anli - composed female': '安莉（沉稳女声）',
  'Long Anlang - clear male': '安朗（清爽男声）',
  'Long Yingmu - refined female': '瑛木（知性女声）',
  'Long Anyang - sunny male': '安阳（阳光男声）',
  'Long Anhuan - upbeat female': '安欢（活泼女声）',
  'Long Hua - sweet female': '华（甜美女生）',
  'Long Cheng - smart male': '成（睿智男声）',
  'Long Ze - warm male': '泽（温暖男声）',
  'Long Xing - gentle female': '星（温柔女声）',
  'Long Tian - rational male': '天（理性男声）',
  'Long Wan - soft female': '婉（软萌女声）',
  'Long Anya - graceful female': '安雅（优雅女声）',
  'Long Anqin - friendly female': '安琴（亲切女声）',
  'Long Anrou - tender female': '安柔（温柔女声）',
  'Long Han - affectionate male': '晗（深情男声）',
  'Kyong - Korean female': 'Kyong（韩语女声）',
  'Riko - Japanese female': 'Riko（日语女声）',
  'Tomoka - Japanese female': 'Tomoka（日语女声）',
  'Long Jiaxin - Cantonese female': '嘉欣（粤语女声）',
  'Long Laotie - Northeastern male': '老铁（东北男声）',
  // ── 豆包/火山 Ark 预设 ──
  '通用女声': '通用女声',
  '通用男声': '通用男声',
  '温柔女声': '温柔女声',
  '知性女声': '知性女声',
  '清新女声': '清新女声',
  '沉稳男声': '沉稳男声',
};

/** voiceId → 中文名映射（不含 description 后缀，用于纯 voiceId 快速查找） */
const VOICE_ID_MAP: Record<string, string> = {
  // CosyVoice
  longxiaochun_v3: '晓春（明亮女声）',
  longxiaoxia_v3: '晓夏（沉静女声）',
  longyumi_v3: 'YUMI（元气少女）',
  longanyun_v3: '安云（温暖男声）',
  longanwen_v3: '安文（优雅女声）',
  longanli_v3: '安莉（沉稳女声）',
  longanlang_v3: '安朗（清爽男声）',
  longyingmu_v3: '瑛木（知性女声）',
  longanyang: '安阳（阳光男声）',
  longanhuan: '安欢（活泼女声）',
  longhua_v3: '华（甜美女生）',
  longcheng_v3: '成（睿智男声）',
  longze_v3: '泽（温暖男声）',
  longxing_v3: '星（温柔女声）',
  longtian_v3: '天（理性男声）',
  longwan_v3: '婉（软萌女声）',
  longanya_v3: '安雅（优雅女声）',
  longanqin_v3: '安琴（亲切女声）',
  longanrou_v3: '安柔（温柔女声）',
  longhan_v3: '晗（深情男声）',
  loongkyong_v3: 'Kyong（韩语女声）',
  loongriko_v3: 'Riko（日语女声）',
  loongtomoka_v3: 'Tomoka（日语女声）',
  longjiaxin_v3: '嘉欣（粤语女声）',
  longlaotie_v3: '老铁（东北男声）',
  // Ark
  BV001_streaming: '通用女声',
  BV002_streaming: '通用男声',
  BV003_streaming: '温柔女声',
  BV004_streaming: '知性女声',
  BV005_streaming: '清新女声',
  BV006_streaming: '沉稳男声',
};

/** 根据 voice 对象或 voiceId 返回中文显示名 */
export function voiceDisplayName(voice: { name?: string; voiceId?: string } | string): string {
  if (typeof voice === 'string') {
    // 纯 voiceId
    if (VOICE_ID_MAP[voice]) return VOICE_ID_MAP[voice];
    return voice;
  }
  if (voice.name && VOICE_NAME_MAP[voice.name]) return VOICE_NAME_MAP[voice.name];
  if (voice.voiceId && VOICE_ID_MAP[voice.voiceId]) return VOICE_ID_MAP[voice.voiceId];
  if (voice.voiceId && VOICE_NAME_MAP[voice.voiceId]) return VOICE_NAME_MAP[voice.voiceId];
  return voice.name || voice.voiceId || '未知';
}

function langLabel(code: string): string {
  return LANGUAGE_LABEL_MAP[code] || code;
}

export function VoicePicker({ t, direction = 'up', refreshTrigger = 0 }: { t: any; direction?: 'up' | 'down'; refreshTrigger?: number }) {
  const { selectedVoiceId, setSelectedVoiceId, favoriteVoices, toggleFavoriteVoice, user } = useApp();
  const [voices, setVoices] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [langFilter, setLangFilter] = useState<string>('all');
  const [catFilter, setCatFilter] = useState<string>('all');
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const loadVoiceList = useCallback(() => {
    setLoadingVoices(true);
    setLoadError(false);
    listVoices()
      .then(data => { setVoices([...data.cloned, ...data.premade]); setLoadError(false); })
      .catch(() => { setLoadError(true); })
      .finally(() => setLoadingVoices(false));
  }, [t]);

  useEffect(() => {
    loadVoiceList();
  }, [refreshTrigger, loadVoiceList]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const languages = useMemo(() => {
    const set = new Set(voices.map(v => v.language).filter(Boolean));
    return ['all', ...Array.from(set)] as string[];
  }, [voices]);

  const filtered = useMemo(() => {
    let list = voices;
    if (search) list = list.filter(v => v.name.toLowerCase().includes(search.toLowerCase()));
    if (langFilter !== 'all') list = list.filter(v => v.language === langFilter);
    if (catFilter !== 'all') list = list.filter(v => v.category === catFilter);
    // Sort: favorites first
    return [...list].sort((a, b) => {
      const aFav = favoriteVoices.includes(a.voiceId) ? -1 : 1;
      const bFav = favoriteVoices.includes(b.voiceId) ? -1 : 1;
      return aFav - bFav;
    });
  }, [voices, search, langFilter, catFilter, favoriteVoices]);

  const currentVoice = voices.find(v => v.voiceId === selectedVoiceId);

  const playPreview = async (voice: any) => {
    if (playingId === voice.voiceId) {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setPlayingId(null);
      return;
    }
    setPlayingId(voice.voiceId);
    // 15s 兜底：TTS 后端卡死时强制清掉 Loader2，避免按钮永远转圈
    const timeoutId = setTimeout(() => {
      setPlayingId(prev => prev === voice.voiceId ? null : prev);
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      toast.error('语音试听超时，请检查豆包/DashScope 密钥或网络');
    }, 15000);
    try {
      const lang = voice.language || 'zh';
      const sampleText = SAMPLE_TEXTS[lang] || SAMPLE_TEXTS.en;
      const buffer = await synthesizeSpeech(sampleText, voice.voiceId, voice.provider, user?.uid);
      clearTimeout(timeoutId);
      // 异步可能结束在另一个 voice 切换后；只有当前仍选中本 voice 才播放
      setPlayingId(prev => {
        if (prev !== voice.voiceId) return prev;
        const blob = new Blob([buffer], { type: 'audio/mp3' });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); };
        audio.onerror = () => { setPlayingId(null); URL.revokeObjectURL(url); };
        audio.play().catch(() => { setPlayingId(null); URL.revokeObjectURL(url); });
        return prev;
      });
    } catch (err) {
      clearTimeout(timeoutId);
      setPlayingId(prev => prev === voice.voiceId ? null : prev);
      toast.error('语音试听失败：' + (err instanceof Error ? err.message : String(err)));
    }
  };

  return (
    <div ref={pickerRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-white/80 text-sm transition-all"
      >
        <Volume2 size={14} className="text-white/40" />
        <span className="max-w-[120px] truncate">{currentVoice ? voiceDisplayName(currentVoice) : (t.selectVoice || '选择语音')}</span>
        <ChevronDown size={14} className={`text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className={`absolute right-0 w-72 max-h-[360px] overflow-hidden rounded-2xl bg-zinc-900/95 backdrop-blur-xl border border-white/10 shadow-2xl z-50 flex flex-col ${direction === 'down' ? 'top-full mt-2' : 'bottom-full mb-2'}`}
          >
            {/* Search */}
            <div className="p-3 border-b border-white/5">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5">
                <Search size={14} className="text-white/55" />
                <input
                  className="bg-transparent text-white/80 text-sm placeholder-white/30 outline-none flex-1"
                  placeholder={t.searchVoices || '搜索语音...'}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2 px-3 py-2 border-b border-white/5">
              <CustomSelect
                value={langFilter}
                onChange={v => setLangFilter(v)}
                size="sm"
                className="w-28"
                options={[
                  { value: 'all', label: t.voiceAllLanguages || '所有语言' },
                  ...languages.filter(l => l !== 'all').map(l => ({ value: l, label: langLabel(l) })),
                ]}
              />
              <CustomSelect
                value={catFilter}
                onChange={v => setCatFilter(v)}
                size="sm"
                className="w-24"
                options={[
                  { value: 'all', label: t.voiceAllCategories || '所有类型' },
                  { value: 'premade', label: t.premadeLabel || '预设' },
                  { value: 'cloned', label: t.clonedLabel || '已克隆' },
                ]}
              />
            </div>

            {/* Voice list */}
            <div className="flex-1 overflow-y-auto">
              {loadingVoices ? (
                <div className="flex items-center justify-center py-8 text-white/55 text-sm">
                  <Loader2 size={16} className="animate-spin mr-2" /> {t.voiceLoading || '加载中...'}
                </div>
              ) : filtered.length === 0 && !loadError ? (
                <div className="flex items-center justify-center py-8 text-white/55 text-sm">
                  {t.voiceNoVoicesFound || '未找到音色'}
                </div>
              ) : filtered.length === 0 && loadError ? (
                <div className="flex flex-col items-center justify-center gap-3 py-8 text-white/55 text-sm">
                  <span>{t.failedToLoadVoices || '加载语音失败'}</span>
                  <button
                    onClick={loadVoiceList}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white/70 text-xs transition-all"
                  >
                    <Loader2 size={12} className={loadingVoices ? 'animate-spin' : ''} />
                    {loadingVoices ? (t.voiceRetrying || '重试中...') : (t.voiceRetry || '重试')}
                  </button>
                </div>
              ) : (
                filtered.map(voice => (
                  <div
                    key={voice.voiceId}
                    onClick={() => { setSelectedVoiceId(voice.voiceId, voice.provider); setOpen(false); }}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-all group ${
                      selectedVoiceId === voice.voiceId
                        ? 'bg-white/10 text-white'
                        : 'text-white/60 hover:bg-white/5 hover:text-white/80'
                    }`}
                  >
                    {/* Favorite */}
                    <button
                      onClick={e => { e.stopPropagation(); toggleFavoriteVoice(voice.voiceId); }}
                      className={`flex-shrink-0 transition-colors ${
                        favoriteVoices.includes(voice.voiceId) ? 'text-amber-400' : 'text-white/45 hover:text-amber-400/60'
                      }`}
                    >
                      <Star size={14} fill={favoriteVoices.includes(voice.voiceId) ? 'currentColor' : 'none'} />
                    </button>

                    {/* Voice info */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{voiceDisplayName(voice)}</div>
                      <div className="flex items-center gap-1 text-xs text-white/55">
                        {voice.provider && <span className="text-white/40">{voice.provider}</span>}
                        {voice.language && <span>· {langLabel(voice.language)}</span>}
                        {voice.category && <span className="opacity-50">· {voice.category}</span>}
                      </div>
                    </div>

                    {/* Play preview */}
                    <button
                      onClick={e => { e.stopPropagation(); playPreview(voice); }}
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                        playingId === voice.voiceId
                          ? 'bg-white/10 text-white'
                          : 'text-white/45 hover:text-white/60 hover:bg-white/10 opacity-0 group-hover:opacity-100'
                      }`}
                    >
                      {playingId === voice.voiceId ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                    </button>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
